import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { resolvePostLoginRedirectStartPage } from '../auth/postLoginRedirect';
import { STATIC_HEADER_ICON_PATH, STATIC_LOGO_PATH } from '../utils/branding';
import { fetchConsolePublicBranding } from '../api/consoleApi';

interface TenantConfig {
  id?: string;
  app_name?: string;
  app_title?: string;
  header_title?: string;
  page_title?: string;
  logo_path?: string;
  favicon_path?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  description?: string;
  company_name?: string;
  company_representative?: string;
  max_file_size?: number;
}

export default function ClassicLoginPage() {
  const { login, isLoading, error } = useAuth();
  const [tenantConfig, setTenantConfig] = useState<TenantConfig>({
    logo_path: STATIC_LOGO_PATH,
    favicon_path: STATIC_HEADER_ICON_PATH,
    company_name: 'Cordexa',
    header_title: 'Sign in to Cordexa',
    app_title: 'Ready to work with your data',
    description: 'Ask questions, explore workflows, connect systems, automate work.',
    primary_color: '#333333',
    secondary_color: '#6a16f0',
    accent_color: '#eff6ff'
  });

  useEffect(() => {
    const determineLoginBranding = async () => {
      try {
        // 1. Check if we have cached tenant config first
        const cachedConfig = localStorage.getItem('tenantConfig');
        if (cachedConfig) {
          const config = JSON.parse(cachedConfig);
          setTenantConfig(prev => ({
            ...prev,
            ...config,
            logo_path: STATIC_LOGO_PATH,
            favicon_path: STATIC_HEADER_ICON_PATH,
          }));
          return;
        }

        // Anonymous console branding for the configured Cordexa tenant (section 10.3).
        // Null company_name from the tenant row must not blank the h1.
        const result = await fetchConsolePublicBranding();
        if (result.status < 400 && result.body && !("reason" in result.body)) {
          const tenantData = result.body as TenantConfig;
          setTenantConfig(prev => ({
            ...prev,
            ...tenantData,
            company_name: tenantData.company_name || prev.company_name,
            description: tenantData.description || prev.description,
            primary_color: tenantData.primary_color || prev.primary_color,
            logo_path: STATIC_LOGO_PATH,
            favicon_path: STATIC_HEADER_ICON_PATH,
          }));
        } else {
          setTenantConfig(prev => ({
            ...prev,
            logo_path: STATIC_LOGO_PATH,
            company_name: 'Cordexa',
            header_title: 'Sign in to Cordexa',
            app_title: 'Ready to work with your data',
            description: 'Ask questions, explore workflows, connect systems, automate work.',
            primary_color: '#111827',
            secondary_color: '#6a16f0',
            accent_color: '#eff6ff'
          }));
        }
      } catch (error) {
        console.error('Error determining login branding:', error);
        // Fallback to Cordexa branding
        setTenantConfig(prev => ({
          ...prev,
          logo_path: STATIC_LOGO_PATH,
          company_name: 'Cordexa',
          header_title: 'Sign in to Cordexa',
          app_title: 'Ready to work with your data',
          description: 'Ask questions, explore workflows, connect systems, automate work.',
          primary_color: '#111827',
          secondary_color: '#6a16f0',
          accent_color: '#eff6ff'
        }));
      }
    };

    determineLoginBranding();
  }, []);

  // Update favicon when tenant config changes
  useEffect(() => {
    if (tenantConfig.favicon_path) {
      const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (faviconLink) {
        faviconLink.href = tenantConfig.favicon_path;
      } else {
        const newFaviconLink = document.createElement('link');
        newFaviconLink.rel = 'icon';
        newFaviconLink.href = tenantConfig.favicon_path;
        document.head.appendChild(newFaviconLink);
      }
    }
  }, [tenantConfig.favicon_path]);

  const handleLogin = async () => {
    try {
      // D37: honour ?return= so auth-death lands on the same conversation.
      await login({
        redirectStartPage: resolvePostLoginRedirectStartPage(
          window.location.search,
          window.location.origin
        ),
      });
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <div
      className="min-h-[100dvh] min-h-screen w-full flex bg-[#070b20] bg-cover bg-center bg-fixed md:items-center md:justify-center md:py-12 md:px-4 lg:px-8"
      style={{ backgroundImage: "url('/assets/cordexa-ciam-login-bg.png')" }}
      data-testid="classic-login"
    >
      <div
        className={[
          "flex w-full flex-col justify-center bg-white",
          "min-h-[100dvh] md:min-h-0",
          "space-y-10 md:space-y-12",
          "pt-[max(2rem,env(safe-area-inset-top,0px))] pb-[max(2rem,env(safe-area-inset-bottom,0px))]",
          "ps-[max(1.25rem,env(safe-area-inset-left,0px))] pe-[max(1.25rem,env(safe-area-inset-right,0px))]",
          "md:p-12",
          "rounded-none shadow-none md:max-w-md md:rounded-[5px] md:shadow-xl md:mx-auto",
        ].join(" ")}
      >
        {/* Logo Section */}
        <div className="text-center">
          <div className="flex justify-center mb-6 md:mb-8">
            <img
              src={STATIC_LOGO_PATH}
              alt={`${tenantConfig.company_name} Logo`}
              className="h-20 w-auto"
            />
          </div>

          <h1
            className="text-3xl md:text-4xl font-bold mb-2"
            style={{ color: tenantConfig.primary_color || '#111827' }}
          >
            Cortex
          </h1>

          <p className="text-gray-600 text-sm leading-relaxed max-w-none md:max-w-sm md:mx-auto">
            Cordexa's operations console
          </p>
        </div>

        {/* Login Section */}
        <div className="space-y-6">
          <button
            type="button"
            onClick={handleLogin}
            disabled={isLoading}
            data-testid="classic-login-sign-in"
            className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:transform-none"
            style={{
              backgroundColor: tenantConfig.primary_color || '#111827',
              boxShadow: `0 4px 14px 0 ${tenantConfig.primary_color || '#111827'}40`
            }}
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Signing in...
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                </svg>
                Sign in with Microsoft
              </div>
            )}
          </button>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Sign in failed
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
